/**
 * net/presence.js — who else is on the midway, and where (GDD 10.1, 10.3).
 *
 * Owns the peer registry, the 10 Hz pose broadcast, the remote goose lifecycle,
 * and the routing table that turns an inbound message into a thing on screen.
 * The transport lives in `peer-manager.js`, the wire rules in `protocol.js`,
 * and the visuals in `remote-goose.js` — this is the part that decides what any
 * of it means.
 *
 * Local player state comes from `gooseController` for position and facing, and
 * from the event bus for everything else (stall entry, throws, mode changes).
 * The direct avatar import is deliberate: it is the one thing with no event to
 * subscribe to, and the avatar layer is finished and imports nothing from here,
 * so the dependency only points one way.
 *
 * @module net/presence
 */

import * as THREE from 'three';
import { EVENTS, NET, THROW_PROFILES } from '../constants.js';
import { bus } from '../core/events.js';
import { save } from '../core/save.js';
import { logCall, options } from '../core/testmode.js';
import { gooseController } from '../avatar/goose-controller.js';
import { peerManager, displayRoom } from './peer-manager.js';
import { laneAnchor, replaySystem, throwKindFor } from './replay.js';
import { GOOSE_TINTS, RemoteGoose } from './remote-goose.js';
import { localGooseName, nameFromId } from './labels.js';
import { badge } from './badge.js';
import { visits } from './visits.js';
import {
  MSG, packBye, packCosmetics, packHello, packPose, packStall, packThrow, unpack,
} from './protocol.js';

class Presence {
  constructor() {
    /** @type {Map<string, RemoteGoose>} */ this.remotes = new Map();
    /** @type {Map<string, number>} peerId → lane index at a shared stall. */
    this.lanes = new Map();
    /** @type {THREE.Group|null} */ this.root = null;

    this.name = 'Goose';
    /** @type {string|null} */ this.activeStall = null;
    this.status = 'offline';
    /** @type {string|null} Display form of the room code. */
    this.room = null;

    this._sendAccum = 0;
    this._nextLane = 0;
    /** @type {Array<(peers:object[])=>void>} */ this._watchers = [];
    /** @type {Array<()=>void>} */ this._unsubs = [];
    this._installed = false;
    this._scratch = new THREE.Vector3();
  }

  /**
   * Build the scene container and subscribe to the local events worth sending.
   * Does not connect — `connect()` is a separate, optional step.
   * @param {THREE.Scene} scene
   * @param {{ npcCount?: () => number }} [opts]
   */
  install(scene, opts = {}) {
    if (this._installed) return this;
    this._installed = true;

    this.name = localGooseName();
    visits.localName = this.name;
    this.npcCount = opts.npcCount || (() => 0);

    this.root = new THREE.Group();
    this.root.name = 'net-remotes';
    scene.add(this.root);

    badge.install();
    badge.onJoin = (code) => this.joinByCode(code);
    this._refreshBadge();

    visits.send = (peerId, message) => peerManager.sendTo(peerId, message);
    peerManager.isFull = () => this.remotes.size >= NET.maxPeers - 1;

    this._unsubs.push(
      peerManager.onMessage((raw, from) => this._receive(raw, from)),
      peerManager.onPeerJoin((id) => this._onPeerJoin(id)),
      peerManager.onPeerLeave((id) => this._onPeerLeave(id)),
      bus.on(EVENTS.STALL_ACTIVATE, ({ stallId }) => this._setStall(stallId)),
      bus.on(EVENTS.STALL_EXIT, () => this._setStall(null)),
      bus.on(EVENTS.THROW, (e) => this._onLocalThrow(e)),
    );

    return this;
  }

  // ── Connection ────────────────────────────────────────────────────────────

  /**
   * Join a carnival. Resolves false rather than throwing when there is nobody
   * to join and nowhere to host — the offline path is not an error.
   * @param {string|null} [roomCode]
   * @returns {Promise<boolean>}
   */
  async connect(roomCode = null) {
    // Test mode never opens a socket. A test that waits on a peer connection is
    // a flaky test, and this is the contract `__cc.net` has always advertised.
    if (options.test) {
      logCall('net', 'presence.connect', [roomCode, 'test-mode']);
      return false;
    }
    if (peerManager.isConnected()) return true;
    this.status = 'connecting';
    this._refreshBadge();

    const result = await peerManager.connect(roomCode);
    this.status = result.connected ? 'connected' : 'offline';
    this.room = result.connected ? displayRoom(result.room) : null;
    this._refreshBadge();

    if (!result.connected) {
      console.log(`[net] offline carnival (${result.reason || 'unavailable'})`);
      return false;
    }

    console.log(`[net] joined ${result.room} as ${result.isHost ? 'host' : 'client'}`);
    this._sayHello();
    return true;
  }

  /** Join a specific room code, reloading so the URL matches what you see. */
  joinByCode(code) {
    const clean = String(code || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    if (!clean) return false;
    const url = new URL(location.href);
    url.searchParams.set('room', clean);
    location.href = url.toString();
    return true;
  }

  disconnect() {
    if (peerManager.isConnected()) peerManager.broadcast(packBye({ reason: 'left' }));
    peerManager.disconnect();
    for (const remote of this.remotes.values()) remote.dispose();
    this.remotes.clear();
    this.lanes.clear();
    replaySystem.clear();
    this.status = 'offline';
    this.room = null;
    this._refreshBadge();
    this._notify();
  }

  /** @returns {boolean} */
  isConnected() {
    return peerManager.isConnected();
  }

  /** @returns {Array<object>} Other geese in the hub. */
  peers() {
    return [...this.remotes.values()].map((r) => ({
      peerId: r.peerId,
      name: r.name,
      position: r.position.toArray(),
      yaw: r.facing,
      mode: r.mode,
      activeStall: r.activeStall,
      laneIndex: r.laneIndex,
    }));
  }

  /**
   * @param {(peers: object[]) => void} cb
   * @returns {() => void} Unsubscribe.
   */
  onPeerUpdate(cb) {
    this._watchers.push(cb);
    return () => { this._watchers = this._watchers.filter((f) => f !== cb); };
  }

  // ── Outbound ──────────────────────────────────────────────────────────────

  /**
   * Send the local goose's pose. Called on the sync tick; the stub-era
   * signature is kept so anything already calling it still compiles.
   * @param {{ position?:number[], yaw?:number, mode?:string }} [pose]
   */
  broadcastPose(pose) {
    if (!peerManager.isConnected()) return;
    const p = pose || this._localPose();
    peerManager.broadcast(packPose({
      position: p.position,
      yaw: p.yaw,
      mode: p.mode,
      activeStall: this.activeStall,
    }));
  }

  /** Re-send cosmetics after an equip change (GDD 3.4). */
  broadcastCosmetics() {
    if (!peerManager.isConnected()) return;
    peerManager.broadcast(packCosmetics({ cosmetics: this._localCosmetics() }));
  }

  /** @private */
  _sayHello() {
    const p = this._localPose();
    peerManager.broadcast(packHello({
      name: this.name,
      cosmetics: this._localCosmetics(),
      position: p.position,
      yaw: p.yaw,
      mode: p.mode,
      activeStall: this.activeStall,
    }));
  }

  /** @private */
  _localPose() {
    const pos = gooseController.getPosition
      ? gooseController.getPosition()
      : this._scratch.set(0, 0, 0);
    return {
      position: [pos.x, pos.y, pos.z],
      yaw: gooseController.facing ?? 0,
      mode: gooseController.getMode ? gooseController.getMode() : 'third',
    };
  }

  /**
   * Read equipped cosmetics from the save rather than the cosmetics singleton,
   * so this is correct before the avatar has mounted anything and after an
   * equip the singleton has not re-applied yet.
   * @private
   */
  _localCosmetics() {
    const equipped = save.data?.cosmetics?.equipped || {};
    const out = {};
    for (const [slot, id] of Object.entries(equipped)) {
      if (id) out[slot] = id;
    }
    return out;
  }

  /** @private */
  _setStall(stallId) {
    this.activeStall = stallId || null;
    if (!this.activeStall) replaySystem.clear();
    if (peerManager.isConnected()) {
      peerManager.broadcast(packStall({ stallId: this.activeStall }));
    }
    this._reseatPeers();
  }

  /**
   * Broadcast a local throw so neighbours can replay it (GDD 10.4).
   * Only the release state travels — never a body, never a contact.
   * @private
   */
  _onLocalThrow(e) {
    if (!peerManager.isConnected() || !e?.stallId) return;
    const velocity = e.velocity?.toArray ? e.velocity.toArray() : e.velocity;
    const origin = e.position?.toArray ? e.position.toArray() : e.position;
    if (!velocity || !origin) return;

    peerManager.broadcast(packThrow({
      stallId: e.stallId,
      origin,
      velocity,
      gravityScale: THROW_PROFILES[e.stallId]?.gravityScale ?? 1,
      kind: throwKindFor(e.stallId, e.handle),
    }));
  }

  // ── Inbound ───────────────────────────────────────────────────────────────

  /** @private */
  _receive(raw, fromId) {
    // Host-relayed traffic addressed to somebody else is not ours to act on.
    if (raw && typeof raw === 'object' && raw._to && raw._to !== peerManager.localId) return;

    const msg = unpack(raw);
    if (!msg) return;
    const remote = this.remotes.get(fromId);

    switch (msg.type) {
      case MSG.HELLO:
        this._upsert(fromId, msg);
        break;
      case MSG.POSE:
        if (!remote) { this._upsert(fromId, msg); break; }
        remote.pushPose(msg.position, msg.yaw);
        remote.mode = msg.mode;
        this._setPeerStall(remote, msg.activeStall);
        break;
      case MSG.COSMETICS:
        remote?.applyCosmetics(msg.cosmetics);
        break;
      case MSG.STALL:
        if (remote) this._setPeerStall(remote, msg.stallId);
        break;
      case MSG.THROW:
        this._replay(fromId, msg);
        break;
      case MSG.VISIT_REQ:
        visits.onRequest(fromId);
        break;
      case MSG.VISIT_DATA:
        visits.onData(fromId, msg.data);
        break;
      case MSG.REACTION:
        visits.onReaction(msg.reaction, msg.fromName);
        break;
      case MSG.BYE:
        this._onPeerLeave(fromId);
        break;
      default:
        break;
    }
  }

  /**
   * Create or refresh a remote goose from a hello.
   * @private
   */
  _upsert(peerId, msg) {
    let remote = this.remotes.get(peerId);
    const isNew = !remote;

    if (isNew) {
      if (this.remotes.size >= NET.maxPeers - 1) return;
      const lane = this._laneFor(peerId);
      remote = new RemoteGoose(peerId, {
        name: msg.name || nameFromId(peerId),
        cosmetics: msg.cosmetics || {},
        color: GOOSE_TINTS[(lane + 1) % GOOSE_TINTS.length],
      });
      remote.laneIndex = lane;
      this.remotes.set(peerId, remote);
      this.root.add(remote.group);

      bus.emit(EVENTS.UI_TOAST, {
        text: `${remote.name} waddled in`, kind: 'good', duration: 2600,
      });
      // Answer a newcomer so they see us too — the host relays it onward.
      if (msg.type === MSG.HELLO) this._sayHello();
    } else if (msg.name) {
      remote.setName(msg.name);
    }

    if (msg.cosmetics) remote.applyCosmetics(msg.cosmetics);
    if (msg.position) remote.pushPose(msg.position, msg.yaw ?? 0);
    if (msg.mode) remote.mode = msg.mode;
    this._setPeerStall(remote, msg.activeStall ?? null);
    this._refreshBadge();
    this._notify();
  }

  /** @private */
  _laneFor(peerId) {
    if (!this.lanes.has(peerId)) this.lanes.set(peerId, this._nextLane++);
    return this.lanes.get(peerId);
  }

  /** @private */
  _setPeerStall(remote, stallId) {
    if (!remote) return;
    remote.activeStall = stallId || null;
    this._seat(remote);
  }

  /**
   * Seat a peer at the neighbouring lane when they are playing the same stall
   * as the local player — otherwise let their broadcast hub pose drive.
   * @private
   */
  _seat(remote) {
    const sharing = this.activeStall && remote.activeStall === this.activeStall;
    remote.setLaneAnchor(sharing ? laneAnchor(this.activeStall, remote.laneIndex) : null);
  }

  /** @private */
  _reseatPeers() {
    for (const remote of this.remotes.values()) this._seat(remote);
  }

  /**
   * Render a peer's throw on their lane — but only if they are at the stall the
   * local player is standing in. A throw at the far end of the midway is not
   * worth a mesh.
   * @private
   */
  _replay(peerId, msg) {
    if (!this.activeStall || msg.stallId !== this.activeStall) return;
    replaySystem.spawn({
      peerId,
      stallId: msg.stallId,
      origin: msg.origin,
      velocity: msg.velocity,
      gravityScale: msg.gravityScale,
      kind: msg.kind,
      laneIndex: this._laneFor(peerId),
    });
  }

  /** @private */
  _onPeerJoin(peerId) {
    // The goose itself appears on their hello; this only greets them.
    if (peerManager.isConnected()) this._sayHello();
  }

  /** @private */
  _onPeerLeave(peerId) {
    const remote = this.remotes.get(peerId);
    if (!remote) return;
    bus.emit(EVENTS.UI_TOAST, {
      text: `${remote.name} waddled off`, kind: 'bad', duration: 2400,
    });
    remote.dispose();
    this.remotes.delete(peerId);
    this.lanes.delete(peerId);
    this._refreshBadge();
    this._notify();
  }

  // ── Frame ─────────────────────────────────────────────────────────────────

  /** @param {number} dt */
  update(dt) {
    const now = performance.now();

    if (peerManager.isConnected()) {
      this._sendAccum += dt * 1000;
      if (this._sendAccum >= NET.syncIntervalMs) {
        this._sendAccum = 0;
        this.broadcastPose();
      }
    }

    for (const [peerId, remote] of this.remotes) {
      remote.update(dt, now);
      if (remote.lastSeen && now - remote.lastSeen > NET.peerTimeoutMs) {
        this._onPeerLeave(peerId);
      }
    }
  }

  /** @private */
  _refreshBadge() {
    badge.set({
      status: this.status,
      peers: this.remotes.size,
      npcs: this.npcCount ? this.npcCount() : 0,
      room: this.room,
      isHost: peerManager.isHost,
    });
  }

  /** @private */
  _notify() {
    const list = this.peers();
    for (const fn of this._watchers) {
      try { fn(list); } catch (err) { console.error('[net] peer watcher threw:', err); }
    }
  }

  /** @returns {object} Test-facing snapshot. */
  state() {
    return {
      installed: this._installed,
      status: this.status,
      connected: peerManager.isConnected(),
      isHost: peerManager.isHost,
      room: this.room,
      name: this.name,
      activeStall: this.activeStall,
      maxPeers: NET.maxPeers,
      peers: this.peers(),
    };
  }

  dispose() {
    for (const off of this._unsubs) off();
    this._unsubs = [];
    this.disconnect();
    this.root?.removeFromParent();
    this.root = null;
    this._installed = false;
  }
}

/** The one presence system. */
export const presence = new Presence();
